import { Enums, Services } from '@bode-canada/core';
import { GridCellCommonPropsType } from '@/ui-kit/components/grid/enums';
import { StyleColorSchemeType } from '@/ui-kit/styles/enums';
import PropertyPathHelper from '@/utils/PropertyPathHelper';
import { SoldDataTableCustomColumnKey } from '../table/enums';
import ListingHelper from '../helpers/ListingHelper';

class TableMapper {
  static getTableRowsFromListings(listings) {
    const listingsGroup = ListingHelper.groupListingsByAddressId(listings);

    return Object.values(listingsGroup).map((groupedListings) => {
      const [firstListings] = groupedListings;

      if (groupedListings.length > 1) {
        return this.#getTableRowFromListing(firstListings, groupedListings);
      }

      return this.#getTableRowFromListing(firstListings);
    });
  }

  static #isBode(listing) {
    return listing?.origin === Enums.ListingOriginEnum.BODE;
  }

  static #getCommonCellColorSchema(listing) {
    if (this.#isBode(listing)) {
      return StyleColorSchemeType.White;
    }

    return '';
  }

  static #getCommonCellFillColorSchema(listing) {
    if (this.#isBode(listing)) {
      return StyleColorSchemeType.Primary;
    }

    return '';
  }

  static #getTransactionsRow(listing, nestedListings = []) {
    const rows = nestedListings.map((nestedListing) => (
      this.#getTableRowFromListing(nestedListing)
    ));

    return {
      [GridCellCommonPropsType.Value]: { rows, addressId: listing.addressId },
      [GridCellCommonPropsType.ColorSchema]: this.#getCommonCellColorSchema(listing),
      [GridCellCommonPropsType.FillColorSchema]: this.#getCommonCellFillColorSchema(listing),
    };
  }

  static #getListingAddress(listing) {
    const listingUnit = Services.CleanupAddressService.getUnit(listing?.unit);

    return `
      ${listingUnit}
      ${listing[SoldDataTableCustomColumnKey.Address]},
      ${listing[SoldDataTableCustomColumnKey.City]}
    `;
  }

  static #getTableRowFromListing(listing, nestedListings) {
    return {
      [SoldDataTableCustomColumnKey.Address]: {
        [GridCellCommonPropsType.Value]: {
          address: this.#getListingAddress(listing),
          href: PropertyPathHelper.generateSoldRouteFromListing(listing),
          isBode: this.#isBode(listing),
        },
        [GridCellCommonPropsType.ColorSchema]: this.#getCommonCellColorSchema(listing),
        [GridCellCommonPropsType.FillColorSchema]: this.#getCommonCellFillColorSchema(listing),
      },
      [SoldDataTableCustomColumnKey.Transactions]: (
        this.#getTransactionsRow(listing, nestedListings)
      ),
      [SoldDataTableCustomColumnKey.Year]: {
        [GridCellCommonPropsType.Value]: listing[SoldDataTableCustomColumnKey.Year],
        [GridCellCommonPropsType.ColorSchema]: this.#getCommonCellColorSchema(listing),
        [GridCellCommonPropsType.FillColorSchema]: this.#getCommonCellFillColorSchema(listing),
      },
      [SoldDataTableCustomColumnKey.Month]: {
        [GridCellCommonPropsType.Value]: listing[SoldDataTableCustomColumnKey.Month],
        [GridCellCommonPropsType.ColorSchema]: this.#getCommonCellColorSchema(listing),
        [GridCellCommonPropsType.FillColorSchema]: this.#getCommonCellFillColorSchema(listing),
      },
      [SoldDataTableCustomColumnKey.ListPrice]: {
        [GridCellCommonPropsType.Value]: listing[SoldDataTableCustomColumnKey.ListPrice],
        [GridCellCommonPropsType.ColorSchema]: this.#getCommonCellColorSchema(listing),
        [GridCellCommonPropsType.FillColorSchema]: this.#getCommonCellFillColorSchema(listing),
      },
      [SoldDataTableCustomColumnKey.SoldPrice]: {
        [GridCellCommonPropsType.Value]: listing[SoldDataTableCustomColumnKey.SoldPrice],
        [GridCellCommonPropsType.ColorSchema]: this.#getCommonCellColorSchema(listing),
        [GridCellCommonPropsType.FillColorSchema]: this.#getCommonCellFillColorSchema(listing),
      },
      [SoldDataTableCustomColumnKey.SoldListRatio]: {
        [GridCellCommonPropsType.Value]: listing[SoldDataTableCustomColumnKey.SoldListRatio],
        [GridCellCommonPropsType.ColorSchema]: this.#getCommonCellColorSchema(listing),
        [GridCellCommonPropsType.FillColorSchema]: this.#getCommonCellFillColorSchema(listing),
      },
      [SoldDataTableCustomColumnKey.BuildingTypes]: {
        [GridCellCommonPropsType.Value]: listing[SoldDataTableCustomColumnKey.BuildingType],
        [GridCellCommonPropsType.ColorSchema]: this.#getCommonCellColorSchema(listing),
        [GridCellCommonPropsType.FillColorSchema]: this.#getCommonCellFillColorSchema(listing),
      },
      [SoldDataTableCustomColumnKey.TotalBeds]: {
        [GridCellCommonPropsType.Value]: listing[SoldDataTableCustomColumnKey.Beds],
        [GridCellCommonPropsType.ColorSchema]: this.#getCommonCellColorSchema(listing),
        [GridCellCommonPropsType.FillColorSchema]: this.#getCommonCellFillColorSchema(listing),
      },
      [SoldDataTableCustomColumnKey.Baths]: {
        [GridCellCommonPropsType.Value]: listing[SoldDataTableCustomColumnKey.Baths],
        [GridCellCommonPropsType.ColorSchema]: this.#getCommonCellColorSchema(listing),
        [GridCellCommonPropsType.FillColorSchema]: this.#getCommonCellFillColorSchema(listing),
      },
      [SoldDataTableCustomColumnKey.FinishedSqft]: {
        [GridCellCommonPropsType.Value]: listing[SoldDataTableCustomColumnKey.FinishedSqft],
        [GridCellCommonPropsType.ColorSchema]: this.#getCommonCellColorSchema(listing),
        [GridCellCommonPropsType.FillColorSchema]: this.#getCommonCellFillColorSchema(listing),
      },
      [SoldDataTableCustomColumnKey.Baths]: {
        [GridCellCommonPropsType.Value]: listing[SoldDataTableCustomColumnKey.Baths],
        [GridCellCommonPropsType.ColorSchema]: this.#getCommonCellColorSchema(listing),
        [GridCellCommonPropsType.FillColorSchema]: this.#getCommonCellFillColorSchema(listing),
      },
      [SoldDataTableCustomColumnKey.Visibility]: {
        [GridCellCommonPropsType.Value]: {
          listingId: listing.listingId,
          isBode: this.#isBode(listing),
        },
        [GridCellCommonPropsType.ColorSchema]: this.#getCommonCellColorSchema(listing),
        [GridCellCommonPropsType.FillColorSchema]: this.#getCommonCellFillColorSchema(listing),
      },
    };
  }
}

export default TableMapper;
